import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import '../styles/home.css';
import { API_BASE, MAPBOX_TILE_URL, MAP_TILE_FALLBACK_URL } from '../utils/api';
import { useTranslation } from '../utils/i18n';

// Fix for default marker icons in Leaflet when used with Webpack/Create React App
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const MapPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(() => localStorage.getItem('authSession') === 'true');
  const position = [52.370216, 4.895168]; // Centered roughly on Amsterdam

  const formatCoordinate = (value) => {
    if (value === null || value === undefined || value === '') return t('Unknown');

    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return String(value);

    return numberValue.toFixed(6);
  };

  const fetchHotels = async () => {
    try {
      const response = await fetch(`${API_BASE}/hotels`);
      if (!response.ok) throw new Error('Failed to fetch hotels');
      const data = await response.json();
      setHotels(data);
    } catch (err) {
      console.error("Error fetching hotels:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const syncAuthSession = async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/session`, {
          credentials: 'include'
        });
        const data = await response.json().catch(() => null);

        if (isCancelled || !data) {
          return;
        }

        if (data.authenticated) {
          localStorage.setItem('authSession', 'true');
          if (data.user) {
            localStorage.setItem('authUser', JSON.stringify(data.user));
          }
          if (data.dhtUser) {
            localStorage.setItem('dhtUser', JSON.stringify(data.dhtUser));
          }
          setIsAuthenticated(true);
          return;
        }

        localStorage.removeItem('authSession');
        localStorage.removeItem('authUser');
        setIsAuthenticated(false);
      } catch {
        if (!isCancelled) {
          setIsAuthenticated(localStorage.getItem('authSession') === 'true');
        }
      }
    };

    syncAuthSession();

    return () => {
      isCancelled = true;
    };
  }, []);

  const handleLogout = async () => {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    }).catch(() => null);

    [
      'authSession',
      'authUser',
      'userRole',
      'dhtUser',
      'selectedOrganization',
      'selectedOrganizationName',
      'selectedRole',
      'selectedRoleName',
      'gdprAccessToken',
      'gdprAccessTokenExp',
      'gdprIdentityToken',
      'gdprIdentityTokenExp'
    ].forEach((key) => localStorage.removeItem(key));
    setIsAuthenticated(false);
  };

  const handleHotelClick = (hotel) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    sessionStorage.setItem('selectedHotelId', String(hotel.DHTHOTid));
    navigate(`/hotel/${hotel.DHTHOTid}`, {
      state: {
        selectedHotelName: hotel.DHTHOT_name,
      },
    });
  };

  return (
    <div className="home-container" style={{ minHeight: '100vh', paddingBottom: '3rem' }}>
      <nav className="navbar navbar-dark home-navbar shadow-sm mb-5">
        <div className="container-fluid px-4 d-flex align-items-center justify-content-center position-relative">
          <span className="navbar-brand mb-0 h1 fw-bold fs-4">Drone Hotel</span>
          <div className="d-flex gap-2 position-absolute end-0">
            <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/')}>{t('Home')}</button>
            <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/manual')}>{t('Manual')}</button>
            {isAuthenticated ? (
              <>
                <button className="btn profile-icon-button" onClick={() => navigate('/profile')} aria-label={t('Open profile')}>
                  <i className="bi bi-person-circle"></i>
                </button>
                <button className="btn btn-outline-light rounded-pill px-4" onClick={handleLogout}>{t('Logout')}</button>
              </>
            ) : (
              <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/login')}>{t('Login')}</button>
            )}
          </div>
        </div>
      </nav>
      
      <div className="container mt-5">
        <div className="text-center mb-5">
          <h1 className="home-title">{t("Welcome to Drone Hotel, we're happy to have you here.")}</h1>
          <p className="home-subtitle">{t('View our locations')}</p>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card home-card">
              <div className="card-body p-2 p-md-4">
                <div style={{ position: 'relative', zIndex: 0, height: '500px', width: '100%', borderRadius: '12px', overflow: 'hidden' }}>
                  <MapContainer center={position} zoom={4} style={{ height: '100%', width: '100%' }}>
                    {MAPBOX_TILE_URL ? (
                      <TileLayer
                        attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url={MAPBOX_TILE_URL}
                        tileSize={512}
                        zoomOffset={-1}
                      />
                    ) : MAP_TILE_FALLBACK_URL ? (
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url={MAP_TILE_FALLBACK_URL}
                      />
                    ) : null}
                    
                    {hotels.map((hotel) => (
                      <Marker 
                        key={hotel.DHTHOTid} 
                        position={[parseFloat(hotel.DHTHOT_latitude), parseFloat(hotel.DHTHOT_longitude)]}
                        eventHandlers={{
                          click: () => {
                            handleHotelClick(hotel);
                          },
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -30]} opacity={1} className="hotel-map-tooltip">
                          <div className="hotel-map-tooltip-content">
                            <strong className="hotel-map-tooltip-title">{hotel.DHTHOT_name}</strong>
                            <span className="hotel-map-tooltip-address">
                              Lat: {formatCoordinate(hotel.DHTHOT_latitude)}
                            </span>
                            <span className="hotel-map-tooltip-address">
                              Long: {formatCoordinate(hotel.DHTHOT_longitude)}
                            </span>
                          </div>
                        </Tooltip>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
                {!MAPBOX_TILE_URL && MAP_TILE_FALLBACK_URL && (
                  <p className="text-muted small mt-3 mb-0">
                    Mapbox tile URL missing in frontend environment, so the map is using the configured fallback.
                  </p>
                )}
                {!MAPBOX_TILE_URL && !MAP_TILE_FALLBACK_URL && (
                  <p className="text-muted small mt-3 mb-0">
                    Map tile URL missing in frontend environment.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
